import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ErrorService } from "@/lib/errors/ErrorService";
import { EnrichedCabinetMember } from "./types";

// Hook to fetch members of a cabinet
export const useCabinetMembers = (cabinetId: string | null) => {
  return useQuery<EnrichedCabinetMember[], Error>({
    queryKey: ["cabinet-members", cabinetId],
    queryFn: async () => {
      if (!cabinetId) return [];

      try {
        // Fetch members via backend API — user identity (email, full_name, avatar_url)
        // is now inlined on each member via the users LEFT JOIN (migration 057/058)
        const members = await apiClient.get<EnrichedCabinetMember[]>(`/cabinets/${cabinetId}/members`);
        return members || [];
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_cabinet_members' }, silent: true });
        // Relancer, ne jamais renvoyer [] : une liste vide ici serait mise en cache
        // comme un succès (staleTime 5 min), écraserait la liste déjà chargée et
        // court-circuiterait les retries du QueryClient. L'écran ne pourrait plus
        // distinguer « aucun membre » de « chargement échoué » (Paramètres affichait
        // « Aucun collaborateur » à un admin lors d'un simple timeout backend).
        throw error;
      }
    },
    enabled: !!cabinetId,
  });
};

// Hook to update a cabinet member
export const useUpdateCabinetMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      cabinet_role,
      title,
      license_exempt,
      feature_flags,
    }: {
      memberId: string;
      cabinet_role?: 'admin' | 'member';
      title?: string | null;
      license_exempt?: boolean;
      feature_flags?: Record<string, boolean> | null;
    }) => {
      // Only include fields explicitly provided by the caller
      const updateData: Record<string, unknown> = {};

      if (cabinet_role !== undefined) updateData.cabinet_role = cabinet_role;
      if (title !== undefined) updateData.title = title;
      if (license_exempt !== undefined) updateData.license_exempt = license_exempt;
      if (feature_flags !== undefined) updateData.feature_flags = feature_flags;

      return await apiClient.patch(`/cabinet-members/${memberId}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cabinet-members"] });
    },
  });
};

/**
 * Récapitulatif de réassignation renvoyé par DELETE /cabinet-members/:id
 * (resource-sharing P3). Le retrait d'un membre NE supprime aucune donnée : ses
 * ressources owned sont réattribuées à un admin restant, ses partages reçus purgés.
 */
export interface MemberRemovalRecap {
  reassignedCases: number;
  reassignedTemplates: number;
  reassignedConversations: number;
  purgedShares: number;
  newOwnerId: string;
  /** true si le partant était le seul admin (repli sur le membre le plus ancien). */
  lastAdminFallback: boolean;
}

// Hook to remove a cabinet member. Returns the reassignment recap so the caller
// can surface a summary toast ("N dossiers réattribués").
export const useRemoveCabinetMember = () => {
  const queryClient = useQueryClient();

  return useMutation<MemberRemovalRecap, Error, string>({
    mutationFn: async (memberId: string) => {
      // apiClient dé-enveloppe déjà {success, data} → on reçoit directement le
      // recap. Un `.data` supplémentaire ici renvoyait undefined et faisait
      // planter le toast récap côté Settings (bug attrapé par l'e2e billing S4).
      return apiClient.delete<{ id: string } & MemberRemovalRecap>(`/cabinet-members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cabinet-members"] });
    },
  });
};
